import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { appointmentService } from '../services/appointmentService';

// ===============================
// Async Thunks
// ===============================

// Create appointment
export const createAppointment = createAsyncThunk(
  'appointments/createAppointment',
  async (appointmentData, { rejectWithValue }) => {
    try {
      const response = await appointmentService.createAppointment(appointmentData);
      return response.data? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Get normal appointment
export const getAppointmentById = createAsyncThunk(
  'appointments/getAppointmentById',
  async (appointmentId, { rejectWithValue }) => {
    try {
      const response = await appointmentService.getAppointmentById(appointmentId);
      return response.data? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Get therapist appointment
export const getTherapistAppointmentById = createAsyncThunk(
  'appointments/getTherapistAppointmentById',
  async (appointmentId, { rejectWithValue }) => {
    try {
      const response = await appointmentService.getAppointmentById(appointmentId);
      return response.data? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update normal appointment slot
export const updateSelectedAppointmentSlot = createAsyncThunk(
  'appointments/updateSelectedAppointmentSlot',
  async ({ appointmentId, slotData }, { rejectWithValue }) => {
    try {
      const response = await appointmentService.updateSelectedAppointmentSlot(
        appointmentId,
        slotData
      );
      return response.data? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update therapist appointment slot (mirrors above)
export const updateSelectedTherapistAppointmentSlot = createAsyncThunk(
  'appointments/updateSelectedTherapistAppointmentSlot',
  async ({ appointmentId, slotData }, { rejectWithValue }) => {
    try {
      // if you have a separate therapist API use that instead
      const response = await appointmentService.updateSelectedAppointmentSlot(
        appointmentId,
        slotData
      );
      return response.data? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ===============================
// Slice
// ===============================

const appointmentSlice = createSlice({
  name: 'appointments',
  initialState: {
    appointments: [],
    selectedAppointment: null,
    selectedTherapistAppointment: null,
    loading: false,
    error: null,
  },

  reducers: {
    clearError: (state) => {
      state.error = null;
    },

    selectAppointment: (state, action) => {
      state.selectedAppointment = action.payload;
    },

    clearSelectedAppointment: (state) => {
      state.selectedAppointment = null;
    },

    // FIXED NAME
    selectTherapistAppointment: (state, action) => {
      state.selectedTherapistAppointment = action.payload;
    },

    clearSelectedTherapistAppointment: (state) => {
      state.selectedTherapistAppointment = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // ===============================
      // Create Appointment
      // ===============================
      .addCase(createAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAppointment.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments.push(action.payload);
      })
      .addCase(createAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===============================
      // Normal Appointment
      // ===============================
      .addCase(getAppointmentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAppointmentById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAppointment = action.payload;
      })
      .addCase(getAppointmentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update selected appointment slot
      .addCase(updateSelectedAppointmentSlot.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSelectedAppointmentSlot.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAppointment = action.payload;
      })
      .addCase(updateSelectedAppointmentSlot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===============================
      // Therapist Appointment
      // ===============================
      .addCase(getTherapistAppointmentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTherapistAppointmentById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTherapistAppointment = action.payload;
      })
      .addCase(getTherapistAppointmentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update therapist appointment slot (mirrors normal)
      .addCase(updateSelectedTherapistAppointmentSlot.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSelectedTherapistAppointmentSlot.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTherapistAppointment = action.payload;
      })
      .addCase(updateSelectedTherapistAppointmentSlot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  selectAppointment,
  clearSelectedAppointment,
  selectTherapistAppointment,
  clearSelectedTherapistAppointment,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;
